/**
 * BillingSettingsPage — the /billing settings hub (design Screen 4).
 *
 * Composes the billing cards:
 *   - CurrentPlanCard      (plan + status; launches the cancel retention modal)
 *   - PaymentMethodCard    ("Manage payment method" → Stripe Customer Portal)
 *   - InvoiceList          (paginated invoice history)
 *   - CancelRetentionModal (Screen 5; "Continue to cancel" → Portal)
 *   - FailedPaymentRetryModal (Screen 7; opened from the past_due state)
 *
 * This page owns the single `useSubscription` call (the cards receive the
 * subscription as a prop rather than each re-fetching — design §6 / plan §6) and
 * the single `useCustomerPortal` call shared by the payment-method card and the
 * "Manage in Stripe" reactivation affordance. Handles loading and error states
 * for the subscription fetch; InvoiceList owns its own loading/error/empty.
 *
 * @param {object} props
 * @param {object} [props.user] - The authenticated user (passed by App shell).
 */

import React, { useState } from 'react';
import PageHeader from '../ui/PageHeader';
import { useSubscription } from '../../hooks/useSubscription';
import { useCustomerPortal } from '../../hooks/useCustomerPortal';
import CurrentPlanCard from './CurrentPlanCard';
import PaymentMethodCard from './PaymentMethodCard';
import InvoiceList from './InvoiceList';
import CancelRetentionModal from './CancelRetentionModal';
import FailedPaymentRetryModal from './FailedPaymentRetryModal';
import styles from './BillingSettingsPage.module.css';

/** Human-readable plan names keyed by the backend `plan_code`. */
const PLAN_LABELS = {
  explorer: 'Explorer',
  fullservice: 'Full Service',
};

export default function BillingSettingsPage() {
  const { subscription, loading, error, refetch } = useSubscription();
  // Single shared portal hook — drives the PaymentMethodCard button + the
  // "Manage in Stripe" reactivation affordance on the plan card.
  const { openPortal, loading: portalLoading, error: portalError } = useCustomerPortal();

  const [cancelOpen, setCancelOpen] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);

  const planCode = subscription?.plan_code || 'explorer';
  const planName = PLAN_LABELS[planCode] || planCode;
  const isPastDue = subscription?.status === 'past_due';

  // Subscription-fetch error (the whole page can't render its plan state).
  if (error && !loading && !subscription) {
    return (
      <div className={styles.page} data-testid="billing-settings">
        <PageHeader eyebrow="Account" title="Billing" />
        <div className={styles.error} role="alert">
          <span>Couldn't load your billing details.</span>
          <button
            type="button"
            className={styles.retry}
            onClick={refetch}
            data-testid="billing-settings-retry"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page} data-testid="billing-settings">
      <PageHeader
        eyebrow="Account"
        title="Billing"
        subtitle="Manage your plan, payment method, and invoices."
      />

      {/* ─── Current plan ─── */}
      <section className={styles.section} aria-labelledby="billing-plan-heading">
        <h2 id="billing-plan-heading" className={styles.sectionTitle}>
          Current plan
        </h2>
        <CurrentPlanCard
          subscription={subscription}
          loading={loading}
          onCancel={() => setCancelOpen(true)}
          onManageInStripe={() => openPortal()}
        />
        {isPastDue && (
          <div className={styles.pastDueActions}>
            <button
              type="button"
              className={styles.warningBtn}
              onClick={() => setRetryOpen(true)}
              data-testid="open-retry-payment-btn"
            >
              Update payment method
            </button>
          </div>
        )}
      </section>

      {/* ─── Payment method ─── */}
      <section className={styles.section} aria-labelledby="billing-pm-heading">
        <h2 id="billing-pm-heading" className={styles.sectionTitle}>
          Payment method
        </h2>
        <PaymentMethodCard
          subscription={subscription}
          onManage={() => openPortal()}
          loading={portalLoading}
          error={portalError}
        />
      </section>

      {/* ─── Invoices ─── */}
      <section className={styles.section} aria-labelledby="billing-invoices-heading">
        <h2 id="billing-invoices-heading" className={styles.sectionTitle}>
          Invoices
        </h2>
        <InvoiceList />
      </section>

      <p className={styles.footnote}>
        Payments are processed securely by Stripe.
      </p>

      <CancelRetentionModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        planName={planName}
        periodEndIso={subscription?.current_period_end}
      />

      <FailedPaymentRetryModal
        open={retryOpen}
        onClose={() => setRetryOpen(false)}
        planName={planName}
      />
    </div>
  );
}
